import { useTranslation } from 'react-i18next';

import { formatSessionDate } from '@/lib/datetime';
import { Link } from 'react-router-dom';

import { AttributedFineRow } from '@/pages/cade/AttributedFineRow';
import { RecordingLink } from '@/pages/cade/RecordingLink';
import { SeiVoteLinks } from '@/pages/cade/SeiVoteLinks';
import { Stat } from '@/pages/cade/Stat';
import type {
  CadeAttributedFine,
  CadeCaseDecision,
  CadeParty,
  CadeVote,
} from '@/services/types';

/**
 * One decision as adjudicated in one session: the item, who sat, how it split,
 * what was fined, who was party, what was cited, and the text as minuted.
 *
 * Drawn on the case page (one card per decision) and on the decision page
 * (one card, expanded) from this single component, so the two cannot describe
 * one decision differently. `standalone` hides the link to the decision page
 * when the card IS the decision page.
 */
export function DecisionCard({
  decision,
  standalone = false,
  caseKey,
}: {
  decision: CadeCaseDecision;
  standalone?: boolean;
  /** The case's digits key. When given, the SEI votes placed under this session are linked. */
  caseKey?: string;
}) {
  const { t, i18n } = useTranslation(['cade']);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });
  const sessionDate = formatSessionDate(decision.session_date, decision.session_date_raw, i18n.language);

  return (
    <article className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">
          {t('cade:case.item', { item: decision.item_number ?? '—' })}
          {decision.is_final_observed ? (
            <span className="ml-2 rounded border px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
              {t('cade:case.final')}
            </span>
          ) : null}
        </h2>
        <span className="text-xs text-muted-foreground">
          {/* The decision's own page: the votes as minuted with their collapsed
              count, and an address a reader can cite. Hidden when this card IS
              that page. */}
          {!standalone ? (
            <Link
              to={`/cade/votes/d/${decision.decision_key}`}
              className="mr-2 text-primary underline-offset-4 hover:underline"
            >
              {t('cade:case.openDecision')}
            </Link>
          ) : null}
          {decision.session_number
            ? t('cade:case.session', {
                number: decision.session_number,
                type: decision.session_type ?? '—',
              })
            : '—'}
          {decision.session_year !== null ? ` · ${decision.session_year}` : ''}
          {sessionDate ? ` · ${sessionDate}` : ''}
          <span className="ml-2 text-xs">
            <RecordingLink date={decision.session_date} sessionType={decision.session_type} />
          </span>
          {/* The one source document a decision can be cited to: the ata it
              was read from, by file name. The archive path stays in the
              tooltip and in the CSV. */}
          {decision.source_file ? (
            <span className="ml-2" title={decision.source_file}>
              {t('cade:case.ata', { file: decision.source_file.split(/[\\/]/).pop() ?? '' })}
            </span>
          ) : null}
        </span>
      </div>
      {caseKey ? <SeiVoteLinks caseKey={caseKey} sessionDate={decision.session_date} /> : null}
      {/* A row without a located "Decisao:" clause records a deferral, a vista
          request or a postponement, not a ruling. Said on the card, because
          the outcome chips below can be empty for two different reasons. */}
      {decision.decisao_found === false ? (
        <p className="rounded border border-dashed bg-muted/30 px-2 py-1 text-xs text-muted-foreground">
          {t('cade:case.noDecisaoClause')}
        </p>
      ) : null}

      <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
        {decision.relator_canonical || decision.relator ? (
          <Stat
            label={t('cade:case.relator')}
            value={
              decision.relator_slug ? (
                <Link to={`/cade/r/${decision.relator_slug}`} className="text-primary underline-offset-4 hover:underline">
                  {decision.relator_canonical ?? decision.relator}
                </Link>
              ) : (
                (decision.relator_canonical ?? decision.relator ?? '—')
              )
            }
          />
        ) : null}
        {decision.is_unanimous !== null ? (
          <Stat
            label={t('cade:case.unanimity')}
            value={
              decision.is_unanimous ? t('cade:case.unanimous') : t('cade:case.byMajority')
            }
          />
        ) : null}
        {decision.voto_vista ? (
          <Stat emphasis={false} label={t('cade:case.votoVista')} value={decision.voto_vista} />
        ) : null}
      </dl>

      <div className="flex flex-wrap gap-1.5">
        {(decision.outcome_codes.length === 0 ? ['UNCLASSIFIED'] : decision.outcome_codes).map(
          (code) => (
            <span key={code} className="rounded border px-2 py-0.5 text-xs text-muted-foreground">
              {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
            </span>
          ),
        )}
        {decision.fines_brl.map((amount, i) => (
          <span
            key={`${amount}-${i}`}
            className="rounded border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs tabular-nums text-foreground"
          >
            {currency.format(amount)}
          </span>
        ))}
      </div>

      {/* The decision page lays the votes out in full below the card. */}
      {standalone ? null : <VotesSection votes={decision.votes} />}
      <AttributedFinesSection fines={decision.attributed_fines} />
      <PartiesSection parties={decision.parties} />
      <CitationsSection citations={decision.legal_citations} />

      {decision.decisao_text ? (
        <details>
          <summary className="cursor-pointer text-xs font-medium text-primary">
            {t('cade:case.ataText')}
          </summary>
          <p className="mt-2 whitespace-pre-wrap border-l-2 pl-3 text-xs text-muted-foreground">
            {decision.decisao_text}
          </p>
        </details>
      ) : null}

      {/* The whole item as minuted, after the clause: header, parties, counsel,
          relator, then the decision. For an item with no clause it is the only
          text and sits beside the "no Decisão clause" flag above. */}
      {decision.segment_text ? (
        <details>
          <summary className="cursor-pointer text-xs font-medium text-primary">
            {t('cade:case.ataItem')}
          </summary>
          <p className="mt-2 whitespace-pre-wrap border-l-2 pl-3 text-xs text-muted-foreground">
            {decision.segment_text}
          </p>
        </details>
      ) : null}
    </article>
  );
}

function VotesSection({ votes }: { votes: CadeVote[] }) {
  const { t } = useTranslation(['cade']);
  if (votes.length === 0) return null;

  return (
    <section className="space-y-1">
      <h3 className="text-xs font-medium text-foreground">
        {t('cade:case.votes', { count: votes.length })}
      </h3>
      <ul className="space-y-0.5 text-xs">
        {votes.map((vote, i) => (
          <li
            key={`${vote.voter_canonical ?? vote.voter}-${i}`}
            className="flex flex-wrap items-baseline justify-between gap-2"
          >
            {/* A link when the vote resolves to a canonical person, plain text
                    otherwise (2 of 350 map to nobody). These are the same people
                    `/cade/r/:slug` is built for, and they rendered as text here
                    while relator names on the very same page were links -- the
                    slug helper's own docstring offered `v.voter_canonical` and no
                    caller had ever taken it. */}
                {vote.voter_slug ? (
                  <Link
                    to={`/cade/r/${vote.voter_slug}`}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {vote.voter_canonical}
                  </Link>
                ) : (
                  <span className="text-foreground">{vote.voter ?? '—'}</span>
                )}
            <span className="text-muted-foreground">
              {t(`cade:overview.positions.${vote.position}`, { defaultValue: vote.position })}
              {vote.target ? ` · ${vote.target}` : ''}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs italic text-muted-foreground">{t('cade:case.votesNotAgreement')}</p>
    </section>
  );
}

function AttributedFinesSection({ fines }: { fines: CadeAttributedFine[] }) {
  const { t, i18n } = useTranslation(['cade']);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });
  if (fines.length === 0) return null;

  return (
    <section className="space-y-1">
      <h3 className="text-xs font-medium text-foreground">{t('cade:case.attributedFines')}</h3>
      <ul className="space-y-1.5 text-xs">
        {fines.map((fine, i) => (
          <li key={`${fine.party_name}-${i}`} className="rounded border p-2">
            {/* The shared row: amount, pattern badge, verified quote or the
                withheld-span flag, extractor and prompt -- drawn identically on
                the sanctions and party pages. Only the SUBJECT differs here.

                The party RECORD page, as of 2026-09-10: `/cade/parties/:slug`
                shows what the archive filed under this exact string, says so
                above its counts, refuses any outcome attribution and never
                totals a fine. Plain text where the attribution resolves to no
                party row (70 of 435 carry only a raw name): the catch-all turns
                a dead link into a valid-looking page, so a missing slug must
                not become one. Branching on the SLUG, not on the name. */}
            <AttributedFineRow
              fine={fine}
              currency={currency}
              subject={
                fine.party_slug ? (
                  <Link
                    to={`/cade/parties/${fine.party_slug}`}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {fine.party_name}
                  </Link>
                ) : (
                  <span className="text-foreground">{fine.party_name || '—'}</span>
                )
              }
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function PartiesSection({ parties }: { parties: CadeParty[] }) {
  const { t } = useTranslation(['cade']);
  if (parties.length === 0) return null;

  const represented = parties.filter((p) => p.role === 'REPRESENTED');
  const applicants = parties.filter((p) => p.role !== 'REPRESENTED');

  return (
    <details>
      <summary className="cursor-pointer text-xs font-medium text-primary">
        {t('cade:case.parties', { count: parties.length })}
      </summary>
      <div className="mt-2 space-y-2 text-xs">
        {represented.length > 0 ? (
          <PartyGroup label={t('cade:case.represented')} parties={represented} />
        ) : null}
        {applicants.length > 0 ? (
          <PartyGroup label={t('cade:case.applicants')} parties={applicants} />
        ) : null}
      </div>
    </details>
  );
}

function CitationsSection({ citations }: { citations: string[] }) {
  const { t } = useTranslation(['cade']);
  if (citations.length === 0) return null;

  return (
    <section className="space-y-1">
      <h3 className="text-xs font-medium text-foreground">{t('cade:case.citations')}</h3>
      {/* Each ground links to the decisions citing it -- the precedent search
          narrowed to this provision, which is where a reader of one decision
          goes next. */}
      <ul className="flex flex-wrap gap-1.5">
        {citations.map((citation) => (
          <li key={citation} className="rounded bg-muted px-2 py-0.5 text-xs">
            <Link
              to={`/cade/decisions?citation=${encodeURIComponent(citation)}`}
              className="text-primary underline-offset-4 hover:underline"
            >
              {citation}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PartyGroup({ label, parties }: { label: string; parties: CadeParty[] }) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <ul className="mt-0.5 space-y-0.5">
        {parties.map((party, i) => (
          <li key={`${party.name}-${i}`} className="text-foreground">
            {/* Linked, like the attributed-fine names a few sections up. This
                page rendered party names two ways -- one linked, one plain --
                and these were the plain ones. */}
            <Link
              to={`/cade/parties/${party.slug}`}
              className="text-primary underline-offset-4 hover:underline"
            >
              {party.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
